import { defineComponent } from 'vue';
import classNames from '../_util/classNames';
import Dialog from '../vc-dialog';
import PropTypes from '../_util/vue-types';
import addEventListener from '../vc-util/Dom/addEventListener';
import CloseOutlined from '@ant-design/icons-vue/CloseOutlined';
import Button from '../button';
import { convertLegacyProps } from '../button/buttonTypes';
import { useLocaleReceiver } from '../locale-provider/LocaleReceiver';
import initDefaultProps from '../_util/props-util/initDefaultProps';
import { canUseDocElement } from '../_util/styleChecker';
import useConfigInject from '../_util/hooks/useConfigInject';
import { getTransitionName } from '../_util/transition';
let mousePosition = null;
// ref: https://github.com/ant-design/ant-design/issues/15795
const getClickPosition = (e) => {
    mousePosition = {
        x: e.pageX,
        y: e.pageY,
    };
    // 100ms 内发生过点击事件，则从点击位置动画展示
    // 否则直接 zoom 展示
    // 这样可以兼容非点击方式展开
    setTimeout(() => (mousePosition = null), 100);
};
// 只有点击事件支持从鼠标位置动画展开
if (canUseDocElement()) {
    addEventListener(document.documentElement, 'click', getClickPosition, true);
}
export const modalProps = () => ({
    prefixCls: String,
    visible: { type: Boolean, default: undefined },
    confirmLoading: { type: Boolean, default: undefined },
    title: PropTypes.any,
    closable: { type: Boolean, default: undefined },
    closeIcon: PropTypes.any,
    onOk: Function,
    onCancel: Function,
    'onUpdate:visible': Function,
    onChange: Function,
    afterClose: Function,
    centered: { type: Boolean, default: undefined },
    width: [String, Number],
    footer: PropTypes.any,
    okText: PropTypes.any,
    okType: String,
    cancelText: PropTypes.any,
    icon: PropTypes.any,
    maskClosable: { type: Boolean, default: undefined },
    forceRender: { type: Boolean, default: undefined },
    okButtonProps: Object,
    cancelButtonProps: Object,
    destroyOnClose: { type: Boolean, default: undefined },
    wrapClassName: String,
    maskTransitionName: String,
    transitionName: String,
    getContainer: {
        type: [String, Function, Boolean, Object],
        default: undefined,
    },
    zIndex: Number,
    bodyStyle: { type: Object, default: undefined },
    maskStyle: { type: Object, default: undefined },
    mask: { type: Boolean, default: undefined },
    keyboard: { type: Boolean, default: undefined },
    wrapProps: Object,
    focusTriggerAfterClose: { type: Boolean, default: undefined },
    modalRender: Function,
});
export const destroyFns = [];
export default defineComponent({
    compatConfig: { MODE: 3 },
    name: 'AModal',
    inheritAttrs: false,
    props: initDefaultProps(modalProps(), {
        width: 520,
        transitionName: 'zoom',
        maskTransitionName: 'fade',
        confirmLoading: false,
        visible: false,
        okType: 'primary',
    }),
    setup(props, { emit, slots, attrs }) {
        const [locale] = useLocaleReceiver('Modal');
        const { prefixCls, rootPrefixCls, direction, getPopupContainer } = useConfigInject('modal', props);
        const handleCancel = (e) => {
            emit('update:visible', false);
            emit('cancel', e);
            emit('change', false);
        };
        const handleOk = (e) => {
            emit('ok', e);
        };
        const renderFooter = () => {
            const { okText = slots.okText?.(), okType, cancelText = slots.cancelText?.(), confirmLoading, } = props;
            return (<>
          <Button onClick={handleCancel} {...props.cancelButtonProps}>
            {cancelText || locale.value.cancelText}
          </Button>
          <Button {...convertLegacyProps(okType)} loading={confirmLoading} onClick={handleOk} {...props.okButtonProps}>
            {okText || locale.value.okText}
          </Button>
        </>);
        };
        return () => {
            const { prefixCls: customizePrefixCls, visible, wrapClassName, centered, getContainer, closeIcon = slots.closeIcon?.(), focusTriggerAfterClose = true, ...restProps } = props;
            const wrapClassNameExtended = classNames(wrapClassName, {
                [`${prefixCls.value}-centered`]: !!centered,
                [`${prefixCls.value}-wrap-rtl`]: direction.value === 'rtl',
            });
            return (<Dialog {...restProps} {...attrs} getContainer={getContainer || getPopupContainer.value} prefixCls={prefixCls.value} wrapClassName={wrapClassNameExtended} visible={visible} mousePosition={mousePosition} onClose={handleCancel} focusTriggerAfterClose={focusTriggerAfterClose} transitionName={getTransitionName(rootPrefixCls.value, 'zoom', props.transitionName)} maskTransitionName={getTransitionName(rootPrefixCls.value, 'fade', props.maskTransitionName)} v-slots={{
                    ...slots,
                    footer: slots.footer || renderFooter,
                    closeIcon: () => {
                        return (<span class={`${prefixCls.value}-close-x`}>
                  {closeIcon || <CloseOutlined class={`${prefixCls.value}-close-icon`}/>}
                </span>);
                    },
                }}></Dialog>);
        };
    },
});
